import { useId, useState } from 'react';

import { cn } from '../cn';
import { Button, ButtonA } from '../Input2';
import { Alerts, Arrows, General } from '../StrokeIcons';
import { carrierFailureBlocksOutcomes, TWILIO_CALL_BLOCKED_CODE, TWILIO_GEO_PERMISSION_CODE } from './softphoneStatus';

export { carrierFailureBlocksOutcomes, TWILIO_CALL_BLOCKED_CODE };

interface CarrierErrorCopy {
  /** What happened, in the rep's words. */
  title: string;
  /** What to do about it. No error codes and no carrier vocabulary — those live in `detail`. */
  body: string;
  /** Concrete "how to fix" steps — only for errors where the fix is on our side of the carrier. */
  steps?: ReadonlyArray<string>;
  /**
   * The technical story, shown only behind "More details": the carrier's code and its own wording,
   * and what that actually means for this call. Absent when the carrier gave no code (nothing more
   * to tell).
   */
  detail?: string;
}

const TWILIO_SUPPORT_URL = 'https://help.twilio.com/';
const TWILIO_TRUST_HUB_URL = 'https://console.twilio.com/us1/develop/trust-hub/customer-profiles';

function twilioErrorDocUrl(code: number): string {
  return `https://www.twilio.com/docs/api/errors/${code}`;
}

/**
 * Plain-language copy per carrier code. Twilio's own text for these is "Phone number is
 * blacklisted" / "invalid phone number" — accurate to Twilio, misleading to a rep, so it is kept
 * out of the message the rep reads first. Each entry says what happened and what to do; the code
 * and Twilio's wording are there for whoever opens the details (an admin, or support).
 */
const CARRIER_ERROR_COPY: Readonly<Record<number, CarrierErrorCopy>> = {
  [TWILIO_CALL_BLOCKED_CODE]: {
    title: 'Twilio blocked this call',
    body:
      'Twilio has restricted outbound calling on your account, so every call will fail the same way until ' +
      'it’s lifted. This isn’t this prospect’s number or your caller ID.',
    steps: [
      'Ask a team admin to open a Twilio support ticket quoting the error details below and the date it started.',
      'In Twilio Trust Hub, make sure the account has an approved Business customer profile — a Starter or Individual profile may no longer qualify for US calls.',
      'Don’t log this as “No answer” — the prospect was never rung.',
    ],
    detail:
      'Twilio error 13225, “Call blocked by Twilio”: Twilio refused to connect the call before it rang. ' +
      'This is an account-level restriction, not anything about this dial.',
  },
  // Twilio's text is "invalid phone number", but the number the rep is looking at is usually well
  // formed — and Twilio Lookup can even report the line as active while the dial is refused (seen
  // in prod, 2026-10-16). What the code really means is that the carrier route to the destination
  // failed: a disconnected line, or a routing problem that clears on its own. The same number
  // dialed again from a different caller id fails identically, so the advice is "retry later",
  // never "switch numbers".
  13224: {
    title: 'This number couldn’t be reached',
    body: 'Try again later. If it keeps failing, mark it as a wrong number so it isn’t dialed again.',
    detail:
      'Twilio error 13224, “invalid phone number”: the phone network couldn’t connect the call to the dialed ' +
      'number. The line may be disconnected, or the route to it may be down right now. The error is about the ' +
      'number being dialed, so your caller ID isn’t the cause and switching it won’t help.',
  },
  // A caller id Twilio doesn't recognise on this account. The 10-minute carrier sync takes a dropped
  // number out of rotation on its own (carrierNumberSync), so this is the window between a number
  // disappearing and the sync noticing — retrying from the same number can't succeed.
  13214: {
    title: 'Your caller ID was rejected',
    body:
      'Twilio doesn’t recognise the number you’re calling from. Use Change to call from a different number, ' +
      'and ask a team admin to check the team’s phone numbers.',
    detail:
      'Twilio error 13214, “Invalid callerId value”: the caller ID on the call isn’t a number this Twilio ' +
      'account owns or has verified. The prospect’s number wasn’t the problem.',
  },
  [TWILIO_GEO_PERMISSION_CODE]: {
    title: 'Calls to this country aren’t enabled',
    body: 'A team admin can enable the country under Voice → Geographic Permissions in the Twilio console.',
    detail:
      'Twilio error 13227: your account’s geographic permissions don’t allow calls to this destination. ' +
      'Every call there fails until the country is enabled.',
  },
};

function copyFor(code: number | null): CarrierErrorCopy {
  const known = code != null ? CARRIER_ERROR_COPY[code] : undefined;
  if (known) return known;
  if (code != null) {
    return {
      title: 'The carrier couldn’t place this call',
      body: 'The call was rejected before it rang. If it happens again, ask a team admin to check the Twilio console.',
      detail: `Twilio error ${code}. Twilio’s documentation for this code says what the carrier reported and whether it is about this number or the account.`,
    };
  }
  // No code: the carrier only reported the leg as failed. That may be a far-end failure, or (on
  // carriers that map every unlisted hangup cause to "failed") an ordinary termination — so make
  // no claim about whether the prospect was reached.
  return {
    title: 'The carrier reported this call as failed',
    body: 'The call didn’t complete normally and the carrier gave no reason. If nobody picked up, log it as usual.',
  };
}

export interface CallFailedNoticeProps {
  /** The carrier's error code, or null when it recorded none. */
  code: number | null;
  className?: string;
}

/**
 * Post-call notice for a dial the CARRIER refused — the case the browser can't see on its own
 * (the WebRTC leg connects, the PSTN bridge is rejected, the rep sees a plain "Call ended"). Says
 * what happened and what to do in plain words; the carrier's code, its own wording and the doc
 * link sit behind a "More details" toggle for whoever needs them. Rendered by every calling
 * surface (floating dialer, inline power-dialer, extension) in place of the bare "Call ended"
 * line; the surface decides whether the outcome picker still makes sense (see
 * `carrierFailureBlocksOutcomes`).
 */
export function CallFailedNotice({ code, className }: CallFailedNoticeProps) {
  const copy = copyFor(code);
  const accountBlock = code === TWILIO_CALL_BLOCKED_CODE;
  // An account block's fix step tells the admin to quote the error, so its details start open.
  const [detailsOpen, setDetailsOpen] = useState(accountBlock);
  const detailsId = useId();
  const detail = code != null && copy.detail != null ? { code, text: copy.detail } : null;

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-left dark:border-red-500/30 dark:bg-red-500/10',
        className,
      )}
      data-test="call-failed-notice">
      {/* The live region is the message only: the details toggle below must not re-announce it. */}
      <div role="alert" className="flex items-start gap-2">
        <Alerts.AlertTriangle className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400" />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-semibold text-red-700 dark:text-red-300" data-test="call-failed-title">
            {copy.title}
          </span>
          <p className="text-xs leading-relaxed text-red-700/90 dark:text-red-200/80">{copy.body}</p>
        </div>
      </div>

      {copy.steps && (
        <ol
          className="list-decimal space-y-1 pl-9 text-xs text-red-700/90 dark:text-red-200/80"
          data-test="call-failed-steps">
          {copy.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}

      {(accountBlock || detail) && (
        <div className="flex flex-wrap items-center gap-2 pl-7">
          {accountBlock && (
            <>
              <ButtonA
                href={TWILIO_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
                color="red"
                tone="outline"
                rIcon={<General.LinkExternal01 />}
                data-test="call-failed-support-link">
                Twilio support
              </ButtonA>
              <ButtonA
                href={TWILIO_TRUST_HUB_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
                color="red"
                tone="outline"
                rIcon={<General.LinkExternal01 />}
                data-test="call-failed-trust-hub-link">
                Trust Hub
              </ButtonA>
            </>
          )}
          {detail && (
            <Button
              size="xs"
              text
              onClick={() => setDetailsOpen((open) => !open)}
              rIcon={<Arrows.ChevronDown className={cn('transition-transform', detailsOpen && 'rotate-180')} />}
              aria-expanded={detailsOpen}
              aria-controls={detailsId}
              data-test="call-failed-details-toggle">
              {detailsOpen ? 'Hide details' : 'More details'}
            </Button>
          )}
        </div>
      )}

      {detail && detailsOpen && (
        <div
          id={detailsId}
          className="flex flex-col gap-2 rounded-md border border-red-200 bg-white/60 p-3 text-xs leading-relaxed text-red-700/90 dark:border-red-500/20 dark:bg-black/20 dark:text-red-200/80"
          data-test="call-failed-details">
          <p>{detail.text}</p>
          <ButtonA
            href={twilioErrorDocUrl(detail.code)}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            text
            className="self-start"
            rIcon={<General.LinkExternal01 />}
            data-test="call-failed-doc-link">
            About error {detail.code}
          </ButtonA>
        </div>
      )}
    </div>
  );
}
